import * as React from 'react';
import cn from 'classnames';
import { useThemeContext } from '../../../utils/providers/theme-context';
import {
  RadioOption,
  useRadioGroup,
  UseRadioGroupOptions
} from '../../../utils/types/use-radio-group';

type RadioGroupProps = Omit<UseRadioGroupOptions, 'options'> & {
  id?: string;
  label?: React.ReactNode;
  options: RadioOption[];
  direction?: 'vertical' | 'horizontal';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  wrapperClass?: string;
  labelClass?: string;
  optionClass?: string;
  error?: string;
};

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (props, ref) => {
    const {
      id,
      label,
      options,
      direction = 'vertical',
      className,
      wrapperClass,
      labelClass,
      optionClass,
      error,
      ...hookOpts
    } = props;

    const { getColour, getBrandTint } = useThemeContext();
    const { name, selected, required, disabled, setSelected, handleKeyDown } =
      useRadioGroup({ ...hookOpts, options });

    return (
      <div className={wrapperClass ?? 'w-full'}>
        {label && (
          <label
            htmlFor={id}
            className={cn('block mb-2 font-semibold text-sm', labelClass)}
            style={{ color: getColour('normalText') }}
          >
            {label}
          </label>
        )}

        <div
          ref={ref}
          id={id}
          role="radiogroup"
          aria-required={required || undefined}
          aria-disabled={disabled || undefined}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={cn(
            direction === 'horizontal'
              ? 'flex flex-wrap gap-2'
              : 'flex flex-col gap-2',
            className
          )}
        >
          {options.map(opt => {
            const checked = selected === opt.value;
            const optDisabled = disabled || !!opt.disabled;
            const inputId = `${name}-${String(opt.value)}`;

            return (
              <label
                key={opt.value}
                htmlFor={inputId}
                className={cn(
                  'relative flex items-start gap-3 px-3.5 py-2.5 rounded-2xl border transition-all duration-150 select-none',
                  optDisabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer',
                  optionClass
                )}
                style={{
                  backgroundColor: checked
                    ? getBrandTint(0.08)
                    : getColour('secondLayer'),
                  borderColor: checked
                    ? getColour('activeText')
                    : getColour('hairline'),
                  boxShadow: checked
                    ? `0 0 0 1px ${getColour('activeText')}`
                    : undefined
                }}
              >
                {/* Hidden native input — keeps form semantics + keyboard nav */}
                <input
                  id={inputId}
                  name={name}
                  type="radio"
                  value={opt.value}
                  checked={checked}
                  disabled={optDisabled}
                  onChange={() => setSelected(opt.value)}
                  aria-describedby={
                    opt.description ? `${inputId}-desc` : undefined
                  }
                  className="sr-only"
                />

                {/* Custom indicator */}
                <span
                  className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-150"
                  style={{
                    borderColor: checked
                      ? getColour('activeText')
                      : getColour('border'),
                    backgroundColor: checked
                      ? getColour('activeText')
                      : 'transparent'
                  }}
                  aria-hidden="true"
                >
                  {checked && (
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: getColour('secondLayer') }}
                    />
                  )}
                </span>

                {/* Text */}
                <div className="flex flex-col min-w-0">
                  <span
                    className="text-sm font-semibold leading-snug"
                    style={{
                      color: checked
                        ? getColour('activeText')
                        : getColour('normalText')
                    }}
                  >
                    {opt.label}
                  </span>
                  {opt.description && (
                    <span
                      id={`${inputId}-desc`}
                      className="text-xs leading-snug mt-0.5"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {opt.description}
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {error && (
          <p className="text-sm mt-1" style={{ color: getColour('danger') }}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
export default RadioGroup;
